import { canonicalizeRegistrarPhoneOrRaw } from '../../../../utils/registrarPhone';
import type { DomainOrderContactRequest } from '../../../../types/models/DomainOrder';

// Stable WizardStep ids — shared between the <Wizard> step definitions and the field-error jump logic.
export const STEP_IDS = {
  search: 'reg-search',
  contact: 'reg-contact',
  nameservers: 'reg-nameservers',
  recap: 'reg-recap',
} as const;

/** How the registrant contact is being entered on the contact step. */
export type ContactMode = 'new' | 'portfolio';

/** How the nameservers are chosen on the nameserver step. */
export type NsMode = 'endpoint' | 'hostnames';

/**
 * The registrant contact form state. Plain strings throughout (controlled inputs); lives in the
 * wizard's page-level state so it survives step navigation (PatternFly unmounts inactive step bodies).
 * `state` is required by the create-order endpoint, even though the standalone contact treats it as
 * optional.
 */
export interface ContactDraft {
  first_name: string;
  last_name: string;
  organization: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zipcode: string;
  countrycode: string;
}

export const EMPTY_CONTACT: ContactDraft = {
  first_name: '',
  last_name: '',
  organization: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zipcode: '',
  countrycode: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Per-field client-side validation for the contact form, keyed by the backend field name. */
export function validateContactDraft(c: ContactDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!c.first_name.trim()) errors.first_name = 'First name is required.';
  if (!c.last_name.trim()) errors.last_name = 'Last name is required.';
  if (!c.email.trim()) errors.email = 'Email is required.';
  else if (!EMAIL_RE.test(c.email.trim())) errors.email = 'Enter a valid email address.';
  if (!c.phone.trim()) errors.phone = 'Phone is required.';
  else if (c.phone.replace(/\D/g, '').length < 6) errors.phone = 'Enter a valid phone number (at least 6 digits).';
  if (!c.address_line1.trim()) errors.address_line1 = 'Address line 1 is required.';
  if (!c.city.trim()) errors.city = 'City is required.';
  if (!c.state.trim()) errors.state = 'State / province is required.';
  if (!c.zipcode.trim()) errors.zipcode = 'Postal code is required.';
  if (!c.countrycode.trim()) errors.countrycode = 'Country is required.';
  return errors;
}

export function isContactDraftValid(c: ContactDraft): boolean {
  return Object.keys(validateContactDraft(c)).length === 0;
}

/**
 * Maps the controlled draft to the create-order `contact` request, trimming + dropping empty
 * optionals. `phone` is canonicalised to the registrar's `+<calling code>.<subscriber>` form — the
 * same rewrite the backend performs on write, so the payload matches what will be stored even when
 * the field never lost focus (autofill, Enter-to-submit). An unparseable value is sent as typed and
 * left for the backend to reject: a payload builder is not a validation gate.
 */
export function buildContactRequest(c: ContactDraft): DomainOrderContactRequest {
  return {
    first_name: c.first_name.trim(),
    last_name: c.last_name.trim(),
    ...(c.organization.trim() ? { organization: c.organization.trim() } : {}),
    email: c.email.trim(),
    phone: canonicalizeRegistrarPhoneOrRaw(c.phone, c.countrycode),
    address_line1: c.address_line1.trim(),
    ...(c.address_line2.trim() ? { address_line2: c.address_line2.trim() } : {}),
    city: c.city.trim(),
    state: c.state.trim(),
    zipcode: c.zipcode.trim(),
    countrycode: c.countrycode.trim().toUpperCase(),
  };
}

// Nameserver hostname rules (the order's `nameservers` field is a flat hostname list — no glue).
export const NS_HOSTNAME_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
export const MIN_NAMESERVERS = 2;
export const MAX_NAMESERVERS = 10;

/** Per-row + form-level validation for the manual nameserver rows. */
export function nameserverIssues(hostnames: string[]): { rowErrors: Record<number, string>; formError: string | null } {
  const rowErrors: Record<number, string> = {};
  const seen = new Set<string>();
  let nonEmpty = 0;
  hostnames.forEach((h, i) => {
    const host = h.trim().toLowerCase();
    if (host === '') return;
    nonEmpty += 1;
    if (!NS_HOSTNAME_RE.test(host)) {
      rowErrors[i] = 'Enter a valid hostname (e.g. ns1.example.com).';
      return;
    }
    if (seen.has(host)) {
      rowErrors[i] = 'Duplicate nameserver hostname.';
    } else {
      seen.add(host);
    }
  });
  const formError = nonEmpty < MIN_NAMESERVERS ? `At least ${MIN_NAMESERVERS} nameservers are required.` : null;
  return { rowErrors, formError };
}

export function areNameserversValid(hostnames: string[]): boolean {
  const { rowErrors, formError } = nameserverIssues(hostnames);
  return formError === null && Object.keys(rowErrors).length === 0;
}

/** Non-empty, trimmed hostnames in entry order (what the create payload sends). */
export function cleanNameservers(hostnames: string[]): string[] {
  return hostnames.map((h) => h.trim()).filter((h) => h !== '');
}

/** A human label for the preview's VAT rate, e.g. "VAT 21%" or "VAT 0% (reverse charge)". */
export function vatRateLabel(vatRate: string): string {
  const n = parseFloat(vatRate);
  if (Number.isNaN(n)) return `VAT ${vatRate}`;
  if (n === 0) return 'VAT 0% (reverse charge)';
  return `VAT ${n}%`;
}
